import React from 'react';
import { ArrowDownCircle, ArrowUpCircle, Factory, History, Package, Pencil, Plus, Trash2 } from 'lucide-react';

import { getProductTypeName } from '@/lib/constants';
import {
  BottomSheet,
  CardAction,
  ChipRow,
  EmptyState,
  PageHeader,
  Pager,
  PhonePage,
  PillButton,
  PrimaryButton,
  ScreenLoader,
  SearchBar,
  StatusPill,
  TabRow,
} from '@/components/phone/primitives';
import { Field, INPUT_CLASS, inputStyle, SelectField } from '@/components/phone/FieldControls';
import { Colors, tone } from '@/components/phone/theme';

/**
 * The catalog, on a phone.
 *
 * The desktop is a seven-column grid with the customer folders in a rail beside
 * it. At a handset's width the rail has nowhere to sit and the grid is a
 * sideways scroll, so here every product is a card that says what it is, who it
 * is for, how much is on the shelf and what it last went for, and the folders
 * are a row of chips that filter the list.
 *
 * Adjusting stock and producing for stock open in a sheet at the thumb rather
 * than a pane beside a table. The state lives on the page; this only draws it,
 * so the phone records exactly what the desktop records.
 */

const STATUS_TABS = [
  { key: 'active', label: 'Active', testID: 'status-tab-active' },
  { key: 'inactive', label: 'Inactive', testID: 'status-tab-inactive' },
];

const STOCK_TONE = { in_stock: 'positive', low_stock: 'caution', out_of_stock: 'critical' };
const STOCK_LABEL = { in_stock: 'in stock', low_stock: 'low', out_of_stock: 'out of stock' };

const REASONS = [
  { value: 'manual_adjustment', label: 'Manual adjustment' },
  { value: 'damage', label: 'Damage' },
  { value: 'return', label: 'Return' },
  { value: 'stock_count', label: 'Stock count' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return dateStr; }
};

function ProductCard({ item, stockState, canViewStock, canManageStock, onOpen, onStock, onProduce, onEdit, onDelete }) {
  const state = stockState(item);
  const price = item.last_price ?? item.default_price ?? item.customer_price;

  return (
    <li
      data-testid={`catalog-card-${item.id}`}
      className="mt-3 rounded-[10px] border"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <button
        type="button"
        onClick={onOpen}
        data-testid={`view-item-${item.id}`}
        className="block w-full px-4 pt-3 text-left"
      >
        <span className="line-clamp-2 block text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
          {item.name}
        </span>
        <span className="mt-0.5 block truncate text-[13px] font-medium" style={{ color: Colors.text.secondary }}>
          {item.customer_name || 'General'}
        </span>
        <span className="mt-0.5 block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
          {getProductTypeName(item.product_type, true)}
        </span>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {canViewStock && (
            <StatusPill
              testID={`stock-pill-${item.id}`}
              label={`${(item.finished_goods_stock || 0).toLocaleString()} ${STOCK_LABEL[state]}`}
              tone={STOCK_TONE[state]}
              dot
            />
          )}
          {item.status && item.status !== 'active' && <StatusPill label={item.status} tone="neutral" />}
        </div>

        <div className="flex items-center justify-between gap-2 py-3 text-[12px]">
          <span style={{ color: Colors.text.secondary }}>
            {canViewStock && item.min_stock_threshold ? `Minimum ${item.min_stock_threshold.toLocaleString()}` : ''}
          </span>
          <span className="font-semibold tabular-nums" style={{ color: Colors.text.primary }}>
            {price ? `₹${Number(price).toLocaleString()}` : '—'}
          </span>
        </div>
      </button>

      <div className="flex items-center gap-2 overflow-x-auto border-t px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" style={{ borderColor: Colors.border.subtle }}>
        {canManageStock && (
          <>
            <CardAction icon={History} label="Stock" onClick={onStock} testID={`stock-item-${item.id}`} />
            <CardAction icon={Factory} label="Produce" onClick={onProduce} testID={`produce-item-${item.id}`} />
          </>
        )}
        <CardAction icon={Pencil} label="Edit" onClick={onEdit} testID={`edit-item-${item.id}`} />
        <CardAction icon={Trash2} label="Delete" tone="critical" onClick={onDelete} testID={`delete-item-${item.id}`} className="ml-auto" />
      </div>
    </li>
  );
}

/** Add or reduce finished goods, with the movements underneath. */
function StockSheet({ item, form, onChange, transactions, transactionsLoading, submitting, onSubmit }) {
  const stock = item.finished_goods_stock || 0;
  const qty = parseInt(form.quantity) || 0;
  const after = stock + (form.type === 'add' ? 1 : -1) * qty;
  const set = (patch) => onChange({ ...form, ...patch });

  return (
    <>
      <div className="flex gap-2" role="group" aria-label="Direction">
        {[['add', 'Add', ArrowUpCircle, 'positive'], ['subtract', 'Reduce', ArrowDownCircle, 'critical']].map(([value, label, Icon, name]) => {
          const on = form.type === value;
          const t = tone(name);
          return (
            <button
              key={value}
              type="button"
              aria-pressed={on}
              data-testid={`adjust-${value}`}
              onClick={() => set({ type: value })}
              className="flex h-12 flex-1 items-center justify-center gap-1.5 rounded-[10px] border text-[14px] font-semibold"
              style={{
                backgroundColor: on ? t.fg : Colors.background.primary,
                borderColor: on ? t.fg : Colors.border.default,
                color: on ? Colors.text.inverse : t.fg,
              }}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          );
        })}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <Field label="Quantity">
          <input
            type="number" inputMode="numeric" value={form.quantity} placeholder="0"
            onChange={(e) => set({ quantity: e.target.value })}
            data-testid="stock-quantity"
            className={`${INPUT_CLASS} text-right tabular-nums`} style={inputStyle}
          />
        </Field>
        <Field label="Reason">
          <SelectField name="reason" label="Reason" value={form.reason} options={REASONS} onChange={(v) => set({ reason: v })} />
        </Field>
      </div>
      <Field label="Notes" className="mt-3">
        <input
          type="text" value={form.notes} onChange={(e) => set({ notes: e.target.value })}
          data-testid="stock-notes" className={INPUT_CLASS} style={inputStyle}
        />
      </Field>
      {qty > 0 && (
        <p className="mt-3 text-[13px]" data-testid="stock-preview" style={{ color: after < 0 ? Colors.status.error : Colors.text.secondary }}>
          {stock.toLocaleString()} → <strong style={{ color: Colors.text.primary }}>{after.toLocaleString()}</strong>
        </p>
      )}
      <PrimaryButton className="mt-4" onClick={onSubmit} busy={submitting} disabled={qty <= 0} testID="record-adjustment">
        Record adjustment
      </PrimaryButton>

      <p className="mb-2 mt-5 text-[11px] font-semibold uppercase tracking-[0.5px]" style={{ color: Colors.text.secondary }}>
        History
      </p>
      {transactionsLoading ? (
        <p className="py-3 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>Loading…</p>
      ) : transactions.length === 0 ? (
        <p className="py-3 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>No movements recorded</p>
      ) : transactions.map((t, i) => {
        const added = (t.adjustment_type || t.type) === 'add';
        return (
          <div key={t.id || i} className="flex items-start justify-between gap-2 border-b py-2.5 last:border-b-0" style={{ borderColor: Colors.border.subtle }}>
            <span className="min-w-0">
              <span className="block truncate text-[13px]" style={{ color: Colors.text.primary }}>
                {t.reason?.replace(/_/g, ' ') || t.type}
              </span>
              <span className="block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
                {t.created_by_name || t.recorded_by_name || '—'} · {formatDate(t.created_at || t.date)}
              </span>
            </span>
            <span className="shrink-0 text-[13px] font-bold tabular-nums" style={{ color: added ? Colors.semantic.positive : Colors.semantic.critical }}>
              {added ? '+' : '−'}{t.quantity}
            </span>
          </div>
        );
      })}
    </>
  );
}

/** Raise an internal order that ends on the shelf rather than at dispatch. */
function ProduceSheet({ item, form, onChange, submitting, onSubmit }) {
  const set = (patch) => onChange({ ...form, ...patch });
  return (
    <>
      <p className="rounded-[10px] px-3 py-2 text-[12px]" style={{ backgroundColor: Colors.background.secondary, color: Colors.text.secondary }}>
        Current stock <strong style={{ color: Colors.text.primary }}>{(item.finished_goods_stock || 0).toLocaleString()}</strong>
        {item.min_stock_threshold ? ` · minimum ${item.min_stock_threshold.toLocaleString()}` : ''}
      </p>
      <Field label="Quantity to produce" className="mt-3">
        <input
          type="number" inputMode="numeric" value={form.quantity} placeholder="0"
          onChange={(e) => set({ quantity: e.target.value })}
          data-testid="produce-quantity"
          className={`${INPUT_CLASS} text-right tabular-nums`} style={inputStyle}
        />
      </Field>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <Field label="Priority">
          <SelectField name="priority" label="Priority" value={form.priority} options={PRIORITIES} onChange={(v) => set({ priority: v })} />
        </Field>
        <Field label="Needed by">
          <input
            type="date" value={form.needed_by} onChange={(e) => set({ needed_by: e.target.value })}
            aria-label="Needed by" className={INPUT_CLASS} style={inputStyle}
          />
        </Field>
      </div>
      <Field label="Remarks" className="mt-3">
        <textarea
          rows={2} value={form.remarks} onChange={(e) => set({ remarks: e.target.value })}
          aria-label="Remarks"
          className="min-h-[64px] w-full resize-y rounded-[10px] border px-3 py-2.5 text-[14px] outline-none"
          style={inputStyle}
        />
      </Field>
      <p className="mt-3 text-[11px] leading-relaxed" style={{ color: Colors.text.tertiary }}>
        This raises an internal order that runs through production like any other job and ends at
        Stock In rather than dispatch. It waits behind customer work unless you raise its priority.
      </p>
      <PrimaryButton className="mt-4" onClick={onSubmit} busy={submitting} disabled={!(parseInt(form.quantity) > 0)} testID="create-production-order">
        Create production order
      </PrimaryButton>
    </>
  );
}

export default function PhoneCatalogList({
  items, total, loading, page, totalPages, onPage, onSearch,
  statusFilter, onStatus, stockFilter, counts, onStockFilter, stockState,
  folders, customerFilter, onFolder,
  canViewStock, canManageStock,
  onNew, onOpen, onEdit, onDelete, onOpenStock, onOpenProduce,
  pane, onClosePane, stockForm, setStockForm, produceForm, setProduceForm,
  transactions, transactionsLoading, submitting, onSubmitStock, onSubmitProduce,
}) {
  const stockChips = [
    { key: null, label: `All ${total}` },
    { key: 'in_stock', label: `In stock ${counts.in_stock}` },
    { key: 'low_stock', label: `Low ${counts.low_stock}` },
    { key: 'out_of_stock', label: `Out ${counts.out_of_stock}` },
  ];
  const folderChips = [
    { key: null, label: 'All products' },
    ...folders.map((folder) => ({
      key: folder.customer_id || folder.id,
      label: `${folder.customer_name || 'Unnamed'} ${folder.items?.length || 0}`,
    })),
  ];

  return (
    <PhonePage testID="product-catalog-page">
      <PageHeader
        title="Product Catalog"
        subtitle={`${total} product${total === 1 ? '' : 's'}`}
        action={(
          <PillButton onClick={onNew} testID="new-product-btn">
            <Plus className="h-4 w-4" /> New
          </PillButton>
        )}
      />

      <div className="px-5">
        <TabRow tabs={STATUS_TABS} active={statusFilter} onSelect={onStatus} />
        <div className="mt-3">
          <SearchBar placeholder="Product name, code…" onChange={onSearch} testID="catalog-search-input" />
        </div>
        {canViewStock && (
          <div className="mt-3">
            <ChipRow chips={stockChips} active={stockFilter} onSelect={onStockFilter} testIDPrefix="stock-chip" />
          </div>
        )}
        {folders.length > 0 && (
          <div className="mt-2">
            <ChipRow chips={folderChips} active={customerFilter} onSelect={onFolder} testIDPrefix="folder-chip" />
          </div>
        )}
      </div>

      <div className="flex-1 px-5 pb-8">
        {loading ? (
          <ScreenLoader />
        ) : items.length === 0 ? (
          <EmptyState icon={Package} message="No products match these filters" hint="Try another folder, or add the first one." />
        ) : (
          <ul data-testid="catalog-list">
            {items.map((item) => (
              <ProductCard
                key={item.id}
                item={item}
                stockState={stockState}
                canViewStock={canViewStock}
                canManageStock={canManageStock}
                onOpen={() => onOpen(item)}
                onStock={() => onOpenStock(item)}
                onProduce={() => onOpenProduce(item)}
                onEdit={() => onEdit(item)}
                onDelete={() => onDelete(item)}
              />
            ))}
          </ul>
        )}
        <Pager page={page} totalPages={totalPages} onPage={onPage} testID="catalog-pagination" />
      </div>

      <BottomSheet
        open={!!pane}
        onClose={onClosePane}
        title={pane?.mode === 'produce' ? 'Produce for stock' : 'Stock'}
        subtitle={pane?.item?.name}
        testID="catalog-sheet"
      >
        {pane && (
          <div className="-mx-5 max-h-[70vh] overflow-y-auto px-5">
            {pane.mode === 'produce' ? (
              <ProduceSheet
                item={pane.item} form={produceForm} onChange={setProduceForm}
                submitting={submitting} onSubmit={onSubmitProduce}
              />
            ) : (
              <StockSheet
                item={pane.item} form={stockForm} onChange={setStockForm}
                transactions={transactions} transactionsLoading={transactionsLoading}
                submitting={submitting} onSubmit={onSubmitStock}
              />
            )}
          </div>
        )}
      </BottomSheet>
    </PhonePage>
  );
}
